import React, { createContext, memo, useEffect, useLayoutEffect, useRef, useState } from "react"
import { FocusScope } from "@react-aria/focus"
import { motion, useMotionValue, useTransform } from "framer-motion"
import { useToggleState } from "react-stately"
// import { useToggleButton } from "react-aria"

import { useDictionary } from "contexts/Dictionary"
import { useHeader } from "contexts/Header"
import { useSubNav } from "contexts/SubNav"
import { useWishlist } from "contexts/Wishlist"

import useScrollLock from "hooks/useScrollLock"

import { Icon } from "components/icon/Icon"
import { Bottom } from "./Bottom"
import { KILL_ROUTE, STEPS } from "./constants"
import { mvSubscribe, useWatchVariations } from "./context"
import { RollerAnimationProvider } from "./context.roller"
import { Footer } from "./Footer"
import { Header } from "./Header"
import { useHash } from "./Hash"
import { Route } from "./Route"
import { StepList } from "./StepList"
import { Close, Main, Marker, Splitter, Starter, Wrapper, Modal, ToggleButton } from "./styles"
import { VarNav } from "./VarNav"
import { Toggle } from "./Toggle"
import { WatchRoller, WatchRollerRef } from "./WatchRoller"
import { Wishlist } from "./Wishlist"

import { useModel } from "../context"

function useItems(steps, collection, getModel, focus, swapping) {
  const rfitems = useRef(null)

  function clear() {
    rfitems.current = null
  }

  function update() {
    const _steps = steps.get()
    if (!_steps) return clear()
    let [_stepindex, _start, ..._items] = _steps.split(",")
    //    alert(_items.slice(1).map(getModel))
    //    _stepindex = +_stepindex
    if (+_stepindex <= 0) {
      swapping.set(_steps)
      focus.set(+_start)
      return clear()
    }
    const unready = _items.map(getModel).filter(v => !v).length
    if (unready) return clear()
    rfitems.current = _items
    swapping.set(_steps)
    focus.set(+_start)
  }

  useEffect(() => mvSubscribe("StepList useItems", steps, update), [])
  useEffect(() => mvSubscribe("StepList useItems", collection, update), [])

  return rfitems
}

function CloseCTA({ ctx, handleClick, ...props }) {
  const { route } = useWatchVariations(ctx)
  const rfmain = useRef(null)

  function display(v) {
    rfmain.current?.classList[!!v ? "add" : "remove"]("wv_reveal")
  }

  useLayoutEffect(() => mvSubscribe("VarNav route", route, display), [])

  return (
    <Close ref={rfmain} onClick={handleClick} {...props}>
      <Icon type='close' />
    </Close>
  )
}

function FocusBlock({ ctx, rctx, name, short_specs }) {
  const { route, fromui, sticky, steps, collection, getModel, active, swapping } = useWatchVariations(ctx)
  const { focus } = useWatchVariations(rctx)
  const [trapfocus, setTrapfocus] = useState(false)
  const rfsticky = useRef(null)
  const dictionary = useDictionary()
  const rfitems = useItems(steps, collection, getModel, focus, swapping)

  const { isVariationOpen } = useModel()

  function onFocus(v) {
    if (swapping.get() || !rfitems.current || v < 0) return
    active.set(rfitems.current[v])
  }

  useEffect(() => mvSubscribe("FocusBlock focus", focus, onFocus), [])

  return (
    <>
      <WatchRoller ctx={ctx} rctx={rctx} />
      <VarNav ctx={ctx} />
      <StepList ctx={ctx} rctx={rctx} />
      <Header ctx={ctx} />
      <Footer ctx={ctx} name={name} short_specs={short_specs} id='mv-watchName' />
      <Bottom ctx={ctx} />
    </>
  )
}

const ModelVariations = memo(({ children, ctx, cta }) => {
  const { route, fromui, hashing, sticky, rfroot, stepindex, toggle } = useWatchVariations(ctx)
  const rctx = createContext(null)
  const rfwrapper = useRef(null)
  const scrollLock = useScrollLock()
  const dictionary = useDictionary()
  const rffreeze = useRef(null)
  const rfmain = useRef(null)
  const rfsticky = useRef(null)

  useHash(route, fromui, hashing)

  function print(v) {
    Array.from({ length: STEPS.length }).map((v, i) => {
      rfroot.current?.classList.remove(`wv_step_${i}`)
    })
    rfroot.current?.classList.add(`wv_step_${v}`)
  }

  //  useEffect(() => mvSubscribe(fetching, init), [])
  useEffect(() => mvSubscribe("", stepindex, print), [])

  function freeze() {
    const el = rfroot.current
    //    header.freeze("wv", 0)
    //    subnav.hide()
    rfroot.current.setAttribute("aria-modal", true) //aria-modal=true traps voiceover, so, only set true when active
    scrollLock.lock(el)

    return () => {
      //      header.unfreeze("wv", 1)
      //      subnav.unhide()
      rfroot.current.setAttribute("aria-modal", false)
      scrollLock.forceUnlock(el)
    }
  }

  function display(v) {
    rfroot.current?.classList[v === KILL_ROUTE ? "add" : "remove"]("wv_kill")
    rfroot.current?.classList[!!v ? "add" : "remove"]("wv_reveal")
    if (v) rffreeze.current = freeze()
    else rffreeze.current?.()
    /*    if (v && !subnav.hide) {
      window.setTimeout(freeze, 40)
    }*/
  }

  useLayoutEffect(() => mvSubscribe("VarNav route", route, display), [])

  useLayoutEffect(() => {
    return () => rffreeze.current?.()
  }, [])

  function onToggle(v) {
    rfmain.current?.classList[v ? "add" : "remove"]("cfg-toggled")
  }

  useLayoutEffect(() => mvSubscribe("", toggle, onToggle), [])

  useLayoutEffect(() => {
    rfroot.current.classList[cta.current ? "remove" : "add"]("mv-nofocus")
  }, [])

  useLayoutEffect(() => {
    const el = rfsticky.current
    const observer = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        sticky.set(!isIntersecting)
      },
      {
        threshold: 0,
      }
    )
    observer.observe(el)

    return () => {
      observer.unobserve(el)
    }
  }, [])

  function onKeyDown(e) {
    rfroot.current.classList.remove("mv-nofocus")
  }

  return (
    <FocusScope contain>
      <Modal ref={rfroot} role='dialog' aria-modal='false' aria-label={dictionary.viewVariations()} className='wvContents' onKeyDownCapture={onKeyDown}>
        <RollerAnimationProvider Ctx={rctx}>
          <Route ctx={ctx} />
          <Splitter ref={rfmain} className='cfg-toggled'>
            <Toggle ctx={ctx} rctx={rctx} />
            <Starter className='wv_ref'>
              <WatchRollerRef ctx={ctx} />
              <Wishlist ctx={ctx} />
              <Footer ctx={ctx} is_entry={true} id='mv-entry-watchName' />
              <Bottom ctx={ctx} is_entry={true} />
            </Starter>
            <Wrapper ref={rfwrapper}>
              <FocusBlock ctx={ctx} rctx={rctx} />
            </Wrapper>
          </Splitter>
          <Marker ref={rfsticky} aria-hidden={true} />
        </RollerAnimationProvider>
        {children}
      </Modal>
    </FocusScope>
  )
})

export default ModelVariations
